import { useState, useRef } from 'react';
import { Button } from './button';
import { Card, CardContent } from './card';
import { Upload, File, X, AlertCircle, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { validationUtils } from '../../utils/validationUtils';

export function FileUpload({ onFileSelect, accept, maxSize = 10, className = '' }) {
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = (selectedFile) => {
    if (!selectedFile) return;
    const validationError = validationUtils.validateFile(selectedFile);
    if (validationError) {
      setError(validationError);
      setFile(null);
      onFileSelect(null);
    } else {
      setError('');
      setFile(selectedFile);
      onFileSelect(selectedFile);
    }
  };

  const handleInputChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) handleFileChange(selectedFile);
    // reset so same file can be re-selected
    event.target.value = '';
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const selectedFile = event.dataTransfer.files[0];
    handleFileChange(selectedFile);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const removeFile = () => {
    setFile(null);
    setError('');
    onFileSelect(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <Card
        className={`border-2 border-dashed transition-all duration-300 cursor-pointer overflow-hidden ${
          isDragging
            ? 'border-primary bg-primary/5'
            : error
            ? 'border-destructive bg-destructive/5'
            : file
            ? 'border-emerald-500 bg-emerald-500/5'
            : 'border-border hover:border-primary/50 hover:bg-primary/5'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
      >
        <CardContent className="p-5 text-center overflow-hidden w-full">
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleInputChange}
            accept={accept || '.pdf,.jpg,.jpeg,.png'}
            className="hidden"
          />

          <AnimatePresence mode="wait">
            {file ? (
              <motion.div
                key="file-selected"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="space-y-3"
              >
                <div className="w-14 h-14 bg-emerald-500 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-7 h-7 text-white" />
                </div>

                <div className="w-full px-2 overflow-hidden">
                  <h3 className="text-base font-semibold text-foreground mb-1">File Selected</h3>
                  {/* Filename — always stays inside the card boundary */}
                  <div className="flex items-center justify-center gap-1.5 w-full overflow-hidden">
                    <File className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
                    <span
                      className="text-sm font-medium text-muted-foreground min-w-0 overflow-hidden"
                      title={file.name}
                      style={{
                        display: 'block',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '100%',
                      }}
                    >
                      {file.name}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{formatFileSize(file.size)}</p>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); removeFile(); }}
                  className="text-destructive hover:text-destructive"
                >
                  <X className="w-4 h-4 mr-2" />
                  Remove File
                </Button>
              </motion.div>
            ) : (
              <motion.div
                key="file-upload"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="space-y-3"
              >
                <div
                  className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto ${
                    error ? 'bg-destructive' : 'bg-primary'
                  }`}
                >
                  {error ? (
                    <AlertCircle className="w-7 h-7 text-destructive-foreground" />
                  ) : (
                    <Upload className="w-7 h-7 text-primary-foreground" />
                  )}
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground mb-1">
                    {isDragging ? 'Drop your file here' : 'Upload your file'}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Tap to browse or drag &amp; drop
                  </p>
                  <div className="text-xs text-muted-foreground space-y-0.5">
                    <p>Supported: PDF, JPG, PNG</p>
                    <p>Max size: {maxSize}MB</p>
                  </div>
                </div>
                <Button variant="outline" size="sm">
                  <Upload className="w-4 h-4 mr-2" />
                  Choose File
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm"
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </motion.div>
      )}
    </div>
  );
}
